const OGC_PIXEL_SIZE_METERS = 0.00028;
const ARCGIS_PIXEL_SIZE_METERS = 0.0254 / 90;
const MAX_LAYER_NAMES = 3;

function cleanServiceUrl(url) {
  return String(url || "")
    .trim()
    .split(/[?#]/, 1)[0]
    .replace(/([^:]\/)\/+/g, "$1")
    .replace(/\/+$/, "");
}

export function resolveWmsEndpoint(url) {
  const endpoint = cleanServiceUrl(url);
  if (!endpoint) return "";
  return /\/(?:wms|ows)$/i.test(endpoint) ? endpoint : `${endpoint}/wms`;
}

export function resolveArcGISMetadataEndpoint(url, layerCode) {
  const endpoint = cleanServiceUrl(url);
  if (!endpoint) return "";
  if (/\/ImageServer$/i.test(endpoint)) return endpoint;

  const layerEndpoint = endpoint.match(/\/(?:MapServer|FeatureServer)\/(\d+)$/i);
  if (layerEndpoint) return endpoint;
  if (!/\/(?:MapServer|FeatureServer)$/i.test(endpoint)) return "";

  const code = layerCode === null || layerCode === undefined || layerCode === ""
    ? "0"
    : String(layerCode);
  return `${endpoint}/${code}`;
}

function nonNegativeNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function openBound(value) {
  const number = nonNegativeNumber(value);
  return number && number > 0 ? number : null;
}

function preferredScale(metadata, effectiveKey, fallbackKey) {
  const effective = nonNegativeNumber(metadata?.[effectiveKey]);
  return effective !== null ? effective : nonNegativeNumber(metadata?.[fallbackKey]);
}

export function normalizeArcGISScaleRange(metadata) {
  if (!metadata || typeof metadata !== "object") return null;
  const minDenominator = openBound(preferredScale(metadata, "effectiveMaxScale", "maxScale"));
  const maxDenominator = openBound(preferredScale(metadata, "effectiveMinScale", "minScale"));
  return minDenominator === null && maxDenominator === null
    ? null
    : { minDenominator, maxDenominator };
}

export function normalizeWmsScaleRange(layerMetadata) {
  if (!layerMetadata || typeof layerMetadata !== "object") return null;
  const minDenominator = openBound(layerMetadata.MinScaleDenominator);
  const maxDenominator = openBound(layerMetadata.MaxScaleDenominator);
  return minDenominator === null && maxDenominator === null
    ? null
    : { minDenominator, maxDenominator };
}

export function findWmsLayer(layerMetadata, layerName) {
  if (!layerMetadata || !layerName) return null;
  if (Array.isArray(layerMetadata)) {
    for (const layer of layerMetadata) {
      const match = findWmsLayer(layer, layerName);
      if (match) return match;
    }
    return null;
  }
  if (layerMetadata.Name === layerName) return layerMetadata;
  return findWmsLayer(layerMetadata.Layer || [], layerName);
}

export function extractWmsScaleRange(capabilities, layerName) {
  const rootLayer = capabilities?.Capability?.Layer;
  return normalizeWmsScaleRange(findWmsLayer(rootLayer, layerName));
}

export function classifyScale(range, scaleDenominator) {
  if (!range || !Number.isFinite(scaleDenominator) || scaleDenominator <= 0) return "unknown";
  if (range.maxDenominator !== null && scaleDenominator > range.maxDenominator) return "zoomIn";
  if (range.minDenominator !== null && scaleDenominator < range.minDenominator) return "zoomOut";
  return "visible";
}

export function scaleDenominatorForView(view, provider) {
  if (!view) return null;
  const resolution = view.getResolution();
  const projection = view.getProjection();
  const metersPerUnit = projection?.getMetersPerUnit?.();
  if (!Number.isFinite(resolution) || !Number.isFinite(metersPerUnit)) return null;
  const pixelSize = provider === "arcgis" ? ARCGIS_PIXEL_SIZE_METERS : OGC_PIXEL_SIZE_METERS;
  return (resolution * metersPerUnit) / pixelSize;
}

function summarizedNames(layers) {
  const names = layers.slice(0, MAX_LAYER_NAMES).map((layer) => `“${layer.name || "Sin nombre"}”`);
  const remaining = layers.length - names.length;
  return remaining > 0 ? `${names.join(", ")} y +${remaining} más` : names.join(", ");
}

export function buildScaleWarning(zoomInLayers, zoomOutLayers) {
  const zoomIn = zoomInLayers || [];
  const zoomOut = zoomOutLayers || [];
  const total = zoomIn.length + zoomOut.length;
  if (!total) return "";

  if (total === 1) {
    const layer = zoomIn[0] || zoomOut[0];
    const direction = zoomIn.length ? "Acércate" : "Aléjate";
    return `La capa “${layer.name || "Sin nombre"}” no es visible en esta escala. ${direction} para visualizarla.`;
  }

  if (!zoomIn.length || !zoomOut.length) {
    const direction = zoomIn.length ? "Acércate" : "Aléjate";
    const affected = zoomIn.length ? zoomIn : zoomOut;
    return `${total} capas no son visibles en esta escala. ${direction} para visualizarlas: ${summarizedNames(affected)}.`;
  }

  return `Hay capas fuera de su rango de escala. Acércate para visualizar: ${summarizedNames(zoomIn)}. Aléjate para visualizar: ${summarizedNames(zoomOut)}.`;
}

export function collectScaleTransitions(evaluatedLayers, layerStates) {
  const zoomInLayers = [];
  const zoomOutLayers = [];
  for (const { layer, status } of evaluatedLayers) {
    const layerId = String(layer.id);
    const previous = layerStates.get(layerId);
    layerStates.set(layerId, status);
    if (status === previous) continue;
    if (status === "zoomIn") zoomInLayers.push(layer);
    if (status === "zoomOut") zoomOutLayers.push(layer);
  }
  return { zoomInLayers, zoomOutLayers };
}
